/**
 * Shared mobile open/hide session flags used by the controller and adapters.
 * Kept separate to avoid circular imports (adapters ↔ controller).
 */

/** Toggled on <html> so CSS can re-enable LiveChat hit-testing while the panel is open. */
export const CHAT_PANEL_OPEN_CLASS = 'sb-chat-panel-open';

let mobileMode = false;
let panelOpen = false;
let hideActiveLauncher: (() => void) | null = null;
const hideRetryTimeouts: number[] = [];

function syncPanelOpenClass(open: boolean): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle(CHAT_PANEL_OPEN_CLASS, open);
}

export function getChatMobileMode(): boolean {
  return mobileMode;
}

export function setSessionMobileMode(isMobile: boolean): void {
  mobileMode = isMobile;
}

export function isChatPanelOpen(): boolean {
  return panelOpen;
}

export function setChatPanelOpen(open: boolean): void {
  panelOpen = open;
  syncPanelOpenClass(open);
}

/** Controller registers so adapters can re-hide without importing the controller. */
export function registerActiveHideLauncher(hide: (() => void) | null): void {
  hideActiveLauncher = hide;
}

export function clearHideRetries(): void {
  if (typeof window === 'undefined') {
    hideRetryTimeouts.length = 0;
    return;
  }
  for (const id of hideRetryTimeouts) {
    window.clearTimeout(id);
  }
  hideRetryTimeouts.length = 0;
}

/** Schedule mobile launcher hide retries; skipped while the panel is open. */
export function scheduleMobileHideRetries(hide: () => void): void {
  if (!mobileMode || typeof window === 'undefined') return;
  clearHideRetries();
  for (const delay of [500, 2000]) {
    const id = window.setTimeout(() => {
      if (panelOpen) return;
      hide();
    }, delay);
    hideRetryTimeouts.push(id);
  }
}

/** Adapters call this on minimize/close (legacy: hide vendor chrome again on mobile). */
export function markChatPanelClosed(): void {
  setChatPanelOpen(false);
  if (mobileMode) {
    hideActiveLauncher?.();
  }
}

export function resetChatSession(): void {
  clearHideRetries();
  mobileMode = false;
  panelOpen = false;
  hideActiveLauncher = null;
  syncPanelOpenClass(false);
}
